import type {
  AdoptSignatureRequest,
  ApiError,
  ChooseFormRequest,
  CodeSent,
  ConsentRequest,
  DeclineRequest,
  ErrorCode,
  LinkInfo,
  SaveValuesRequest,
  SaveValuesResult,
  SessionView,
  SignRequest,
  Values,
  VerifyRequest,
  VerifyResult,
  ViewedRequest,
  UploadFileRequest,
} from "@gsignatures/core";
import { ApiFailure, type PreviewResult, type SignerApi } from "./types";

const KNOWN: ReadonlySet<string> = new Set<ErrorCode>([
  "invalid_link",
  "session_required",
  "wrong_step",
  "invalid_values",
  "preview_mismatch",
  "rate_limited",
  "paused",
  "not_found",
  "conflict",
  "bad_request",
  "server_error",
]);

function codeForStatus(status: number): ErrorCode {
  if (status === 401) return "session_required";
  if (status === 404) return "invalid_link";
  if (status === 409) return "conflict";
  if (status === 429) return "rate_limited";
  if (status >= 500) return "server_error";
  return "bad_request";
}

function newKey(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }
}

/** The signer API over HTTP: every path under /api/s/{token}, the session in an httpOnly cookie. */
export class HttpSignerApi implements SignerApi {
  readonly fake = false;
  private readonly base: string;

  constructor(token: string, origin = (import.meta.env.VITE_API_BASE as string | undefined) ?? "") {
    this.base = `${origin}/api/s/${encodeURIComponent(token)}`;
  }

  private async send(method: string, path: string, body?: unknown, accept = "application/json"): Promise<Response> {
    const headers: Record<string, string> = { Accept: accept };
    if (body !== undefined) headers["Content-Type"] = "application/json";
    if (method !== "GET") headers["Idempotency-Key"] = newKey();
    let res: Response;
    try {
      res = await fetch(this.base + path, {
        method,
        headers,
        credentials: "include",
        cache: "no-store",
        referrerPolicy: "no-referrer",
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });
    } catch (e) {
      throw new ApiFailure("network", e instanceof Error ? e.message : "Network error");
    }
    if (!res.ok) {
      let payload: Partial<ApiError> | null = null;
      try {
        payload = (await res.json()) as Partial<ApiError>;
      } catch {
        payload = null;
      }
      const code = payload?.error && KNOWN.has(payload.error) ? payload.error : codeForStatus(res.status);
      throw new ApiFailure(code, payload?.message ?? res.statusText, res.status, payload?.details);
    }
    return res;
  }

  private async json<T>(method: string, path: string, body?: unknown): Promise<T> {
    const res = await this.send(method, path, body);
    try {
      return (await res.json()) as T;
    } catch {
      throw new ApiFailure("server_error", "The server's answer couldn't be read.", res.status);
    }
  }

  private async none(method: string, path: string, body?: unknown): Promise<void> {
    await this.send(method, path, body);
  }

  link = () => this.json<LinkInfo>("GET", "");
  sendCode = () => this.json<CodeSent>("POST", "/code");
  verify = (code: string) => this.json<VerifyResult>("POST", "/verify", { code } satisfies VerifyRequest);
  session = () => this.json<SessionView>("GET", "/session");
  consent = (version: string) => this.json<SessionView>("POST", "/consent", { version } satisfies ConsentRequest);
  viewed = (id: string, sha256: string) => this.none("POST", `/documents/${encodeURIComponent(id)}/viewed`, { sha256 } satisfies ViewedRequest);
  fileUrl = (id: string) => `${this.base}/documents/${encodeURIComponent(id)}/file`;
  accept = (id: string) => this.json<SessionView>("POST", `/documents/${encodeURIComponent(id)}/accept`);
  chooseForm = (id: string, usPerson: boolean) =>
    this.json<SessionView>("POST", `/forms/${encodeURIComponent(id)}/choose`, { usPerson } satisfies ChooseFormRequest);
  saveValues = (id: string, values: Values) =>
    this.json<SaveValuesResult>("PATCH", `/forms/${encodeURIComponent(id)}/values`, { values } satisfies SaveValuesRequest);
  review = () => this.json<SessionView>("POST", "/review");
  adoptSignature = (req: AdoptSignatureRequest) => this.json<SessionView>("POST", "/signature", req);
  certificationShown = (id: string) => this.none("POST", `/documents/${encodeURIComponent(id)}/certification-shown`);
  sign = (id: string, previewSha256: string) =>
    this.json<SessionView>("POST", `/documents/${encodeURIComponent(id)}/sign`, { previewSha256 } satisfies SignRequest);
  downloadUrl = (id: string) => `${this.base}/documents/${encodeURIComponent(id)}/download`;
  decline = (reason?: string) => this.json<LinkInfo>("POST", "/decline", (reason ? { reason } : {}) satisfies DeclineRequest);
  paper = () => this.json<LinkInfo>("POST", "/paper");
  addPicture = (id: string, dataUrl: string, name?: string) =>
    this.json<SessionView>("POST", `/uploads/${encodeURIComponent(id)}/files`, { dataUrl, ...(name ? { name } : {}) } satisfies UploadFileRequest);
  removePicture = (id: string, fileId: string) => this.json<SessionView>("DELETE", `/uploads/${encodeURIComponent(id)}/files/${encodeURIComponent(fileId)}`);
  pictureUrl = (id: string, fileId: string) => `${this.base}/uploads/${encodeURIComponent(id)}/files/${encodeURIComponent(fileId)}`;

  preview = async (id: string): Promise<PreviewResult> => {
    const res = await this.send("GET", `/documents/${encodeURIComponent(id)}/preview`, undefined, "application/pdf");
    const sha256 = res.headers.get("X-Preview-Sha256");
    if (!sha256) throw new ApiFailure("server_error", "The preview came without its fingerprint.", res.status);
    return { sha256, blob: await res.blob() };
  };
}
